import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '../api.js';
import { useNotifyStore } from '../store/notifyStore.js';
import {
  PlusIcon,
  TrashIcon,
  ClockIcon,
  PersonIcon,
  VideoIcon,
  CopyIcon,
  CheckIcon,
  SearchIcon,
  LinkIcon,
  RefreshIcon,
  UsersIcon,
  ArrowRightIcon,
  SovcombankLogo,
} from '../components/Icons.jsx';

const REFRESH_INTERVAL_MS = 4000;

const STATUS_CONFIG = {
  active: {
    label: 'Активна',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500 animate-pulse',
  },
  waiting: {
    label: 'В ожидании',
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  ended: {
    label: 'Завершена',
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    dot: 'bg-slate-400',
  },
  expired: {
    label: 'Истёкла',
    badge: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
  },
};

/** @param {number} seconds */
function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** @param {string} isoStr */
function formatDateTime(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

/**
 * Modern Room Card Component - Sovcombank Style
 * @param {object} props
 * @param {import('../api.js').Room} props.room
 * @param {(id: string, role: 'operator'|'client') => void} props.onEnter
 * @param {(id: string) => Promise<void>} props.onDelete
 */
export function RoomCard({ room, onEnter, onDelete }) {
  const notify = useNotifyStore((s) => s.notify);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedClient, setCopiedClient] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const statusCfg = STATUS_CONFIG[room.status] || STATUS_CONFIG.ended;
  const clientUrl = `${window.location.origin}/#/room/${room.id}`;
  const displayId = room.guid || room.id;

  const handleCopyClientLink = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(clientUrl).then(() => {
      setCopiedClient(true);
      notify('success', 'Ссылка для клиента скопирована');
      setTimeout(() => setCopiedClient(false), 2000);
    }).catch(() => {
      notify('info', clientUrl);
    });
  };

  const handleCopyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(displayId).then(() => {
      setCopiedId(true);
      notify('success', 'ID комнаты скопирован');
      setTimeout(() => setCopiedId(false), 2000);
    });
  };

  return (
    <div className="group relative bg-white hover:bg-white border border-slate-200/90 hover:border-[#002882]/40 rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 flex flex-col justify-between">
      {/* Top Bar: Status Badge + Delete Button */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusCfg.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
              {statusCfg.label}
            </span>
            {room.status === 'active' && (
              <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                LIVE
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                title="Удалить комнату"
              >
                <TrashIcon size={15} />
              </button>
            ) : (
              <div className="flex items-center gap-1.5 animate-fade-in">
                <button
                  type="button"
                  onClick={() => onDelete(room.id).finally(() => setConfirmDelete(false))}
                  className="px-2.5 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors shadow-sm"
                >
                  Да, удалить
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs transition-colors"
                >
                  Отмена
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Room ID and Title */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <h4
              onClick={handleCopyId}
              title="Нажмите, чтобы скопировать ID"
              className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-[#002882] transition-colors cursor-pointer truncate font-mono"
            >
              {displayId}
            </h4>
            <button
              type="button"
              onClick={handleCopyId}
              className="text-slate-400 hover:text-[#002882] transition-colors"
              title="Скопировать ID"
            >
              {copiedId ? <CheckIcon size={14} className="text-emerald-600" /> : <CopyIcon size={14} />}
            </button>
          </div>

          {room.contactId && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-600">
              <PersonIcon size={13} className="text-[#002882]" />
              <span>Контакт: <strong className="text-slate-900 font-semibold">{room.contactId}</strong></span>
            </div>
          )}
        </div>

        {/* Meta details */}
        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mb-5 bg-[#f8fafc] rounded-xl p-3 border border-slate-150">
          <div className="flex items-center gap-1.5 truncate">
            <ClockIcon size={13} className="text-slate-400 shrink-0" />
            <span>Создана: {formatDateTime(room.createdAt)}</span>
          </div>
          <div className="flex items-center gap-1.5 truncate">
            <UsersIcon size={13} className="text-slate-400 shrink-0" />
            <span>Клиентов: {room.clientCount ?? 0}</span>
          </div>
          {room.durationSeconds ? (
            <div className="col-span-2 flex items-center gap-1.5 text-[#002882] font-mono font-medium">
              <span>Длительность: {formatDuration(room.durationSeconds)}</span>
            </div>
          ) : null}
        </div>

        {/* Quick Share Link for Client */}
        <div className="mb-5">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Ссылка для клиента
          </label>
          <div className="flex items-center gap-1.5 bg-[#f8fafc] border border-slate-200 rounded-xl p-1.5 pl-3">
            <span className="text-xs font-mono text-slate-700 truncate flex-1">
              {clientUrl}
            </span>
            <button
              type="button"
              onClick={handleCopyClientLink}
              className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all shrink-0 active:scale-95"
            >
              {copiedClient ? <CheckIcon size={13} className="text-emerald-600" /> : <CopyIcon size={13} />}
              <span>{copiedClient ? 'Скопировано' : 'Копировать'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <button
          type="button"
          onClick={() => onEnter(room.id, 'operator')}
          className="flex-1 py-2.5 px-4 rounded-xl bg-[#002882] hover:bg-[#001f66] text-white font-semibold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-sm shadow-[#002882]/20 active:scale-[0.98] transition-all"
        >
          <VideoIcon size={16} />
          <span>{room.status === 'active' ? 'Войти в звонок' : 'Войти как оператор'}</span>
        </button>

        <button
          type="button"
          onClick={() => onEnter(room.id, 'client')}
          title="Открыть окно клиента (для проверки звука и видео)"
          className="py-2.5 px-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs sm:text-sm flex items-center gap-1.5 border border-slate-200 active:scale-[0.98] transition-all"
        >
          <span>Тест клиента</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Main Rooms Dashboard Component - Sovcombank Theme
 * @param {object} props
 * @param {(id: string, role: 'operator'|'client') => void} props.onEnterRoom
 */
export default function Rooms({ onEnterRoom }) {
  const notify = useNotifyStore((s) => s.notify);
  const [rooms, setRooms] = useState(/** @type {import('../api.js').Room[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'waiting' | 'ended'
  const [quickJoinInput, setQuickJoinInput] = useState('');

  const loadRooms = useCallback(async () => {
    try {
      const data = await api.listRooms();
      setRooms(data.rooms ?? []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRooms();
    const timer = setInterval(loadRooms, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadRooms]);

  const handleCreateRoom = useCallback(async () => {
    setCreating(true);
    try {
      const newRoom = await api.createRoom({
        contact_id: `client_${Math.floor(1000 + Math.random() * 9000)}`,
      });
      notify('success', 'Новая комната создана!');
      await loadRooms();
      if (newRoom && newRoom.id) {
        onEnterRoom(newRoom.id, 'operator');
      }
    } catch (err) {
      notify('error', `Ошибка создания комнаты: ${err.message}`);
    } finally {
      setCreating(false);
    }
  }, [notify, loadRooms, onEnterRoom]);

  const handleDeleteRoom = useCallback(async (id) => {
    try {
      await api.deleteRoom(id);
      notify('success', 'Комната удалена');
      await loadRooms();
    } catch (err) {
      notify('error', `Не удалось удалить комнату: ${err.message}`);
    }
  }, [notify, loadRooms]);

  const handleQuickJoin = (e) => {
    e.preventDefault();
    const raw = quickJoinInput.trim();
    if (!raw) return;

    // Extract ID if a full URL was pasted
    let roomId = raw;
    const match = raw.match(/room\/([a-zA-Z0-9_-]+)/);
    if (match) roomId = match[1];

    onEnterRoom(roomId, 'operator');
  };

  // Stats
  const stats = useMemo(() => {
    const total = rooms.length;
    const active = rooms.filter((r) => r.status === 'active').length;
    const waiting = rooms.filter((r) => r.status === 'waiting').length;
    const ended = rooms.filter((r) => r.status === 'ended' || r.status === 'expired').length;
    return { total, active, waiting, ended };
  }, [rooms]);

  // Filtered rooms
  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      // Status filter
      if (statusFilter === 'active' && r.status !== 'active') return false;
      if (statusFilter === 'waiting' && r.status !== 'waiting') return false;
      if (statusFilter === 'ended' && r.status !== 'ended' && r.status !== 'expired') return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const idMatch = (r.id || '').toLowerCase().includes(q);
        const guidMatch = (r.guid || '').toLowerCase().includes(q);
        const contactMatch = (r.contactId || '').toLowerCase().includes(q);
        const nameMatch = (r.clientName || '').toLowerCase().includes(q);
        return idMatch || guidMatch || contactMatch || nameMatch;
      }
      return true;
    });
  }, [rooms, statusFilter, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8 animate-fade-in">
      {/* ── Hero Banner (Sovcombank Branding) ── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-[#f0f4fc]/80 to-[#e4edf9]/90 border border-[#d1dcf0] p-6 sm:p-10 shadow-sm backdrop-blur-xl">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#002882]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#ea1c24]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-[#002882] text-xs font-semibold border border-[#d1dcf0] shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Сервер видеоконсультаций онлайн
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-[#002882] tracking-tight leading-tight">
              Видеоконсультации Совкомбанка
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Организация защищенных сессий видеосвязи с клиентами, демонстрация документов и дистанционное банковское обслуживание.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            <button
              type="button"
              onClick={handleCreateRoom}
              disabled={creating}
              className="py-3.5 px-6 rounded-2xl bg-[#002882] hover:bg-[#001f66] text-white font-bold text-sm sm:text-base flex items-center justify-center gap-2.5 shadow-lg shadow-[#002882]/25 active:scale-[0.98] transition-all"
            >
              <PlusIcon size={20} />
              <span>{creating ? 'Создание комнаты…' : 'Создать комнату'}</span>
            </button>
          </div>
        </div>

        {/* Live Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-8 pt-6 border-t border-[#d1dcf0]/70">
          <div className="bg-white/90 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 shadow-sm">
            <div className="text-xs text-slate-500 font-medium">Всего комнат</div>
            <div className="text-xl sm:text-3xl font-bold text-[#002882] mt-1 font-mono">{stats.total}</div>
          </div>
          <div className="bg-white/90 rounded-2xl p-3.5 sm:p-4 border border-emerald-200 shadow-sm">
            <div className="text-xs text-emerald-700 font-medium flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Активных звонков
            </div>
            <div className="text-xl sm:text-3xl font-bold text-emerald-600 mt-1 font-mono">{stats.active}</div>
          </div>
          <div className="bg-white/90 rounded-2xl p-3.5 sm:p-4 border border-amber-200 shadow-sm">
            <div className="text-xs text-amber-700 font-medium flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              Ожидают звонка
            </div>
            <div className="text-xl sm:text-3xl font-bold text-amber-600 mt-1 font-mono">{stats.waiting}</div>
          </div>
          <div className="bg-white/90 rounded-2xl p-3.5 sm:p-4 border border-slate-200/80 shadow-sm">
            <div className="text-xs text-slate-500 font-medium">Завершенные</div>
            <div className="text-xl sm:text-3xl font-bold text-slate-600 mt-1 font-mono">{stats.ended}</div>
          </div>
        </div>
      </div>

      {/* ── Search, Filter & Quick Join Toolbar ── */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {[
            { id: 'all', label: 'Все', count: stats.total },
            { id: 'active', label: 'Активные', count: stats.active },
            { id: 'waiting', label: 'В ожидании', count: stats.waiting },
            { id: 'ended', label: 'Завершенные', count: stats.ended },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
                statusFilter === tab.id
                  ? 'bg-[#002882] text-white shadow-sm shadow-[#002882]/20'
                  : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 shadow-sm'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                statusFilter === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search & Quick Join */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 lg:max-w-xl justify-end">
          {/* Search Box */}
          <div className="relative flex-1">
            <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ID или контакту…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#002882] focus:ring-2 focus:ring-[#002882]/10 transition-all shadow-sm"
            />
          </div>

          {/* Quick Join form */}
          <form onSubmit={handleQuickJoin} className="flex items-center gap-1.5">
            <input
              type="text"
              value={quickJoinInput}
              onChange={(e) => setQuickJoinInput(e.target.value)}
              placeholder="ID комнаты…"
              className="w-32 sm:w-36 px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#002882] focus:ring-2 focus:ring-[#002882]/10 transition-all shadow-sm"
            />
            <button
              type="submit"
              className="py-2.5 px-3 rounded-xl bg-[#002882] hover:bg-[#001f66] text-white text-xs font-semibold flex items-center gap-1 shadow-sm active:scale-95 transition-all shrink-0"
              title="Перейти в комнату"
            >
              <ArrowRightIcon size={14} />
              <span>Войти</span>
            </button>
          </form>
        </div>
      </div>

      {/* ── Room Cards Grid ── */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-64 rounded-2xl bg-white border border-slate-200/80 animate-pulse p-6 space-y-4 shadow-sm">
              <div className="h-6 w-24 bg-slate-100 rounded-full" />
              <div className="h-5 w-48 bg-slate-100 rounded-lg" />
              <div className="h-20 bg-slate-50 rounded-xl" />
              <div className="h-10 bg-slate-100 rounded-xl" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-6 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-center text-sm shadow-sm">
          Ошибка загрузки списка комнат: {error}
        </div>
      ) : filteredRooms.length === 0 ? (
        <div className="p-12 text-center rounded-3xl bg-white border border-slate-200 shadow-sm space-y-4 max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-[#f0f4fc] text-[#002882] flex items-center justify-center mx-auto ring-8 ring-[#f0f4fc]/50">
            <UsersIcon size={32} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {searchQuery ? 'Комнаты не найдены' : 'Комнат пока нет'}
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-relaxed">
              {searchQuery
                ? 'Попробуйте изменить поисковый запрос или сбросить фильтры.'
                : 'Создайте новую комнату для консультации клиента в один клик.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCreateRoom}
            disabled={creating}
            className="btn-primary mx-auto !rounded-xl !py-2.5 text-xs sm:text-sm"
          >
            <PlusIcon size={16} />
            <span>Создать комнату</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredRooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onEnter={onEnterRoom}
              onDelete={handleDeleteRoom}
            />
          ))}
        </div>
      )}
    </div>
  );
}